import React from 'react';

const DayView = ({ date, events }) => {
  // Generate hours from 7 AM to 11 PM
  const hours = [];
  for (let hour = 7; hour <= 23; hour++) {
    hours.push(hour);
  }

  // Get events for the selected date
  const getEventsForDate = () => {
    if (!date || !events) return { bookings: [], availability: [], adminEvents: [] };
    
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    
    const bookings = events.filter(e => 
      e.type === 'booking' && e.date === dateStr
    );
    
    const availability = events.filter(e => 
      e.type === 'availability' && e.date === dateStr
    );
    
    const adminEvents = events.filter(e => 
      e.type === 'admin_event' && 
      dateStr >= e.start_date && 
      dateStr <= e.end_date
    );
    
    return { bookings, availability, adminEvents };
  };

  // Get events for a specific hour
  const getEventsForHour = (hour) => {
    const dateEvents = getEventsForDate();
    const hourStr = String(hour).padStart(2, '0');
    
    const hourBookings = dateEvents.bookings.filter(b => {
      if (!b.start_time) return false;
      const bookingHour = parseInt(b.start_time.split(':')[0]);
      return bookingHour === hour;
    });
    
    const hourAvailability = dateEvents.availability.filter(a => {
      if (!a.time_slots) return false;
      return a.time_slots.some(slot => {
        const slotHour = parseInt(slot.start_time.split(':')[0]);
        return slotHour === hour;
      });
    });
    
    return { bookings: hourBookings, availability: hourAvailability, adminEvents: dateEvents.adminEvents };
  };

  const formatDate = (dateStr) => {
    const d = typeof dateStr === 'string' ? new Date(dateStr + 'T00:00:00') : dateStr;
    const days = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
    const months = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December'];
    return `${days[d.getDay()]}, ${d.getDate()}. ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const dateEvents = getEventsForDate();

  return (
    <div style={{
      background: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      {/* Date header */}
      <div style={{
        background: '#7c6f5e',
        color: 'white',
        padding: '1.5rem',
        fontSize: '1.3rem',
        fontWeight: 600
      }}>
        📅 {formatDate(date)}
      </div>

      {/* Admin events banner (if any) */}
      {dateEvents.adminEvents.length > 0 && (
        <div style={{
          padding: '1rem',
          background: '#e3f2fd',
          borderBottom: '2px solid #1976d2'
        }}>
          {dateEvents.adminEvents.map((event, i) => (
            <div key={i} style={{
              padding: '0.5rem',
              background: event.color || '#1976d2',
              color: 'white',
              borderRadius: '5px',
              marginBottom: i < dateEvents.adminEvents.length - 1 ? '0.5rem' : 0,
              fontWeight: 500
            }}>
              🎯 {event.title}
              {event.description && (
                <div style={{ fontSize: '0.85rem', marginTop: '0.25rem', opacity: 0.9 }}>
                  {event.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Hour rows */}
      <div>
        {hours.map((hour) => {
          const hourEvents = getEventsForHour(hour);
          const hasEvents = hourEvents.bookings.length > 0 || hourEvents.availability.length > 0;
          
          return (
            <div
              key={hour}
              style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr',
                borderBottom: '1px solid #e5e5e5',
                minHeight: '60px',
                background: hasEvents ? '#fafafa' : 'white'
              }}
            >
              {/* Time column */}
              <div style={{
                padding: '1rem',
                fontSize: '1rem',
                fontWeight: 600,
                color: '#666',
                borderRight: '2px solid #e5e5e5',
                display: 'flex',
                alignItems: 'flex-start',
                background: '#f9f9f9'
              }}>
                {String(hour).padStart(2, '0')}:00
              </div>
              
              {/* Events column */}
              <div style={{
                padding: '0.75rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                {/* Bookings */}
                {hourEvents.bookings.map((booking, i) => {
                  // Determine color based on instructor and type
                  let bgColor = '#e8f5e9';
                  let borderColor = '#2e7d32';
                  let textColor = '#2e7d32';
                  
                  if (booking.instructor_id === 'oscar') {
                    bgColor = '#e3f2fd';  // Blå for Oscar
                    borderColor = '#1976d2';
                    textColor = '#1976d2';
                  } else if (booking.instructor_id === 'julie-christiansen' && booking.booking_type === 'greven') {
                    bgColor = '#e8f5e9';  // Grøn for Julie Heiden
                    borderColor = '#2e7d32';
                    textColor = '#2e7d32';
                  } else if (booking.booking_type === 'weekend_course' || booking.booking_type === 'weekend-external') {
                    bgColor = '#f3e5f5';  // Lilla for kurser
                    borderColor = '#7b1fa2';
                    textColor = '#7b1fa2';
                  }
                  
                  return (
                    <div
                      key={`booking-${i}`}
                      style={{
                        padding: '0.75rem',
                        background: bgColor,
                        borderLeft: `4px solid ${borderColor}`,
                        borderRadius: '4px'
                      }}
                    >
                      <div style={{
                        fontWeight: 600,
                        color: textColor,
                        marginBottom: '0.25rem',
                        fontSize: '0.95rem'
                      }}>
                        📅 {booking.start_time} - Booking
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#333' }}>
                        <strong>{booking.client_name}</strong>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                        Instruktør: {booking.instructor_name}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        Type: {booking.booking_type} • {booking.duration_minutes} min
                      </div>
                      {booking.payment_status && (
                        <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                          <span style={{
                            padding: '0.2rem 0.5rem',
                            background: booking.payment_status === 'paid' ? '#4caf50' : '#ff9800',
                            color: 'white',
                            borderRadius: '3px',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            {booking.payment_status === 'paid' ? 'Betalt' : 'Afventer betaling'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Availability */}
                {hourEvents.availability.length > 0 && hourEvents.bookings.length === 0 && (
                  <div
                    style={{
                      padding: '0.75rem',
                      background: '#fff3e0',
                      borderLeft: '4px solid #f57c00',
                      borderRadius: '4px'
                    }}
                  >
                    <div style={{
                      fontWeight: 600,
                      color: '#f57c00',
                      fontSize: '0.95rem'
                    }}>
                      ⏰ Tilgængelig
                    </div>
                    {hourEvents.availability.map((avail, i) => (
                      <div key={i} style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                        {avail.instructor_id === 'julie-christiansen' ? 'Julie Christiansen' : 
                         avail.instructor_id === 'oscar' ? 'Óskar Erik' : avail.instructor_id}
                        {avail.time_slots && (
                          <span style={{ marginLeft: '0.5rem' }}>
                            ({avail.time_slots.filter(s => parseInt(s.start_time.split(':')[0]) === hour).map(s => s.start_time).join(', ')})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Empty slot */}
                {!hasEvents && (
                  <div style={{
                    padding: '0.75rem',
                    color: '#ccc',
                    fontSize: '0.9rem',
                    fontStyle: 'italic'
                  }}>
                    Ingen aktiviteter
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DayView;
